import { useEffect, useRef, useState } from 'react'
import { apiFetch } from './api'

export const LOCATION_PING_INTERVAL_MS = 60 * 1000 // 1 minute

/**
 * While the user is signed in, reads the browser GPS position every minute and
 * stores it on the server. The backend keeps only the latest position.
 *
 * Returns { coords, error } where coords is { latitude, longitude, accuracy }.
 */
export function useLocationPing(enabled) {
  const [coords, setCoords] = useState(null)
  const [error, setError] = useState(null)
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !('geolocation' in navigator)) return

    let cancelled = false

    async function pingOnce() {
      if (inFlightRef.current) return
      inFlightRef.current = true
      navigator.geolocation.getCurrentPosition(
        async ({ coords: c }) => {
          inFlightRef.current = false
          if (cancelled) return
          const next = { latitude: c.latitude, longitude: c.longitude, accuracy: c.accuracy }
          setCoords(next)
          setError(null)
          try {
            await apiFetch('/location/ping', { method: 'POST', body: JSON.stringify(next) })
          } catch { /* offline — the next tick retries */ }
        },
        (err) => {
          inFlightRef.current = false
          if (!cancelled) setError(err.message || 'Location unavailable')
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      )
    }

    pingOnce()
    const timer = setInterval(pingOnce, LOCATION_PING_INTERVAL_MS)
    // Refresh as soon as the tab comes back to the foreground
    const onVisible = () => { if (document.visibilityState === 'visible') pingOnce() }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [enabled])

  return { coords, error }
}
